import {
  ChangeEvent,
  ComponentProps,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Data } from '@pcmp/react-coworker-components';
import { ListBoxFooter } from '@ingka/list-box';
import { ListViewItem } from '@ingka/list-view';
import Button from '@ingka/button';
import Search from '@ingka/search';
import chevronDown from '@ingka/ssr-icon/paths/chevron-down';

export type DataItem = { value: string; text: string };

type AllowedControl = 'radiobutton' | 'checkbox';

type DropdownPickerProps = {
  position?: ComponentProps<typeof PillDropdown>['position'];
  confirm?: boolean;
  disabled?: boolean;
  data: DataItem[];
  emptyLabel: string;
  search?: boolean;
  value: DataItem['value'][];
  onChange: (value: DataItem['value'][]) => void;
  control: AllowedControl;
  selectAllLabel?: string;
  maxSelected?: number;
};

type DropdownPickerSearchProps = {
  value: string;
  onChange: (value: string) => void;
};

const { PillDropdown } = Data;

// This component is used to render confirm button in the dropdown list
const ConfirmFooter: FC<{
  onClick: () => void;
}> = ({ onClick }) => {
  const { t } = useTranslation();

  return (
    <ListBoxFooter>
      <Button
        fluid
        size="small"
        text={t('label.apply')}
        type="primary"
        onClick={onClick}
        data-testid="applyDropdownPicker"
      />
    </ListBoxFooter>
  );
};

// This component is used to render each item in the dropdown list
const DropdownPickerItem: FC<{
  title: string;
  checked?: boolean;
  disabled?: boolean;
  indeterminate?: boolean;
  value: string;
  controlName: string;
  control: 'radiobutton' | 'checkbox';
  onChange: (value: string) => void;
}> = ({
  title,
  checked,
  disabled,
  value,
  onChange,
  controlName,
  control,
  indeterminate,
}) => {
  const controlProps = useMemo(
    () => ({
      value,
      checked,
      indeterminate,
      disabled,
    }),
    [checked, value, indeterminate, disabled]
  );
  const handleChange = useCallback(() => onChange(value), [onChange]);

  return (
    <ListViewItem
      control={control}
      controlProps={controlProps}
      inset
      showDivider={false}
      name={controlName}
      onChange={handleChange}
      title={title}
    />
  );
};

// This component is used to filter data based on search string
const DropdownPickerSearch: FC<DropdownPickerSearchProps> = ({
  value,
  onChange,
}) => {
  const { t } = useTranslation();
  const handleChange = (
    event: ChangeEvent<HTMLInputElement>,
    { value }: { scope?: string; value: string } = { value: '' }
  ) => {
    onChange(value);
  };

  return (
    <div className="absolute top-4 px-6 w-full">
      <Search
        data-testid="searchDropdownPicker"
        ariaLabel={t('label.search')}
        clearBtnText={t('label.search')}
        id="search"
        value={value}
        onChange={handleChange}
        onClear={handleChange}
        submitBtnText="Submit"
      />
    </div>
  );
};

export const DropdownPicker: FC<DropdownPickerProps> = ({
  value,
  onChange,
  position,
  confirm,
  disabled,
  data,
  emptyLabel,
  search,
  control,
  selectAllLabel,
  maxSelected,
}) => {
  const { t } = useTranslation();
  // State
  const [open, setOpen] = useState(false);
  const [searchString, setSearchString] = useState('');
  const name = data
    .filter((item) => value.includes(item.value))
    .map((item) => item.text)
    .join(', ');
  // When confirm is true, use draft value, otherwise use the value from the props
  const [valueDraft, setValueDraft] = useState(value);
  const currentValue = confirm ? valueDraft : value;
  // When floating dropdown is closed, reset draft value and search string
  const onClose = () => {
    setOpen(false);
    setValueDraft(value);
    setSearchString('');
  };
  // Change floating dropdown visibility
  const onToggle = () => setOpen(!open);
  // When one of the controls is changed, dispatch new value of the selected items
  const handleControlChange = (newValue: string) => {
    const changeFn = confirm ? setValueDraft : onChange;
    let arrValues = [newValue];

    if (control === 'checkbox') {
      arrValues = currentValue.includes(newValue)
        ? currentValue.filter((val) => val !== newValue)
        : [...currentValue, newValue];
    }

    changeFn(arrValues);
  };
  // When "Select All" control is changed, dispatch new value of the selected items
  const handleAllControlsChange = () => {
    const changeFn = confirm ? setValueDraft : onChange;
    let arrayValues = data.map(({ value }) => value);
    const uncheckAll = currentValue.length === data.length;

    if (control === 'checkbox' && uncheckAll) {
      arrayValues = [];
    }

    changeFn(arrayValues);
  };
  // When confirm button is clicked, dispatch new value of the selected items
  const onConfirmValue = () => {
    onChange(currentValue);
    setOpen(false);
    setSearchString('');
  };
  // This random string is used to create unique control name for each dropdown
  const controlName = useRef(`${Date.now()}`);
  // Filter data based on search string
  const filteredData = useMemo(
    () => data.filter(({ text }) => text.match(new RegExp(searchString, 'i'))),
    [data, searchString]
  );

  // Update draft if root state changes
  useEffect(() => {
    setValueDraft(value);
  }, [value]);

  return (
    <PillDropdown
      showEllipsis={true}
      pill={{
        disabled,
        iconPosition: 'trailing',
        label: `${name}` || emptyLabel,
        size: 'small',
        ssrIcon: chevronDown,
      }}
      listBox={{
        className: `mt-3 pt-[85px] max-h-[450px] bottom-[5px] `,
        footer: (
          <>
            {search && (
              <DropdownPickerSearch
                value={searchString}
                onChange={setSearchString}
              />
            )}
            {maxSelected && (
              <div className="absolute top-[75px] px-6 w-full">
                <p className="text-xs text-right">
                  *{t('label.maxItems', { max: maxSelected })}
                </p>
              </div>
            )}
            {confirm && <ConfirmFooter onClick={onConfirmValue} />}
          </>
        ),
      }}
      position={position}
      open={open}
      onClick={onToggle}
      onClose={onClose}
    >
      {selectAllLabel && (
        <DropdownPickerItem
          control={control}
          value=""
          checked={currentValue.length === data.length}
          title={selectAllLabel}
          indeterminate={
            currentValue.length > 0 && currentValue.length < data.length
          }
          onChange={handleAllControlsChange}
          controlName={controlName.current}
        />
      )}
      {filteredData.map(({ value, text }) => (
        <DropdownPickerItem
          key={value}
          control={control}
          checked={currentValue.includes(value)}
          value={value}
          title={text}
          onChange={handleControlChange}
          controlName={controlName.current}
          disabled={
            maxSelected && currentValue.length >= maxSelected
              ? !currentValue.includes(value)
              : false
          }
        />
      ))}
    </PillDropdown>
  );
};
